import {
  createSlice,
  createSelector,
  createEntityAdapter,
  PayloadAction,
} from '@reduxjs/toolkit';

// Import the stable selector from walletSlice
import { selectAllBalanceEntities } from './walletSlice';

/**
 * TOKEN SLICE - Redux Toolkit for Token Metadata
 *
 * This slice stores token metadata keyed by 'network:address' for direct address lookups.
 * No balance data is stored here - only token metadata.
 */

// Token metadata interface (no balance data)
interface TokenMetadata {
  id: string; // 'network:address' (our internal key)
  network: string;
  chain?: string;
  address: string;
  name?: string;
  symbol?: string;
  decimals?: number;
  logoURI?: string;
  description?: string;
  projectURI?: string;
  programId?: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  dashboardImage?: string;
  giftCardImages?: Array<{
    imageUrl: string;
    createdAt: string;
  }>;
  categories?: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  isPartner?: boolean;
  usdPrice?: number;
}

// Payload interfaces
interface UpsertTokenPayload {
  network: string;
  address: string;
  chain?: string;
  name?: string;
  symbol?: string;
  decimals?: number;
  logoURI?: string;
  description?: string;
  projectURI?: string;
  programId?: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  dashboardImage?: string;
  giftCardImages?: Array<{
    imageUrl: string;
    createdAt: string;
  }>;
  categories?: Array<{
    id: number;
    name: string;
    description: string;
  }>;
  isPartner?: boolean;
  usdPrice?: number;
}

interface UpsertTokensPayload {
  tokens: Array<{
    network?: string;
    address: string;
    chain?: string;
    name?: string;
    symbol?: string;
    decimals?: number;
    logoURI?: string;
    description?: string;
    projectURI?: string;
    programId?: string;
    active?: boolean;
    createdAt?: string;
    updatedAt?: string;
    dashboardImage?: string;
    giftCardImages?: Array<{
      imageUrl: string;
      createdAt: string;
    }>;
    categories?: Array<{
      id: number;
      name: string;
      description: string;
    }>;
    isPartner?: boolean;
    usdPrice?: number;
  }>;
}

// ── Adapter ──────────────────────────────────────────────────
// Tokens keyed by 'network:address'
const tokensAdapter = createEntityAdapter<TokenMetadata>();

// ── State ──────────────────────────────────────────────────
interface TokenState {
  tokens: ReturnType<typeof tokensAdapter.getInitialState>;
  ui: {
    isLoading: boolean;
    error: string | null;
    lastUpdated: number | null;
  };
}

const initialState: TokenState = {
  tokens: tokensAdapter.getInitialState(),
  ui: { isLoading: false, error: null, lastUpdated: null },
};

// ── Helper Functions ────────────────────────────────────────────────
const tokenKey = (network: string, address: string) => `${network}:${address}`;

// ── Slice ──────────────────────────────────────────────────
const tokenSlice = createSlice({
  name: 'tokens',
  initialState,
  reducers: {
    // Token operations
    upsertToken: (state, action: PayloadAction<UpsertTokenPayload>) => {
      const { network, address, ...tokenData } = action.payload;
      const id = tokenKey(network, address);

      tokensAdapter.upsertOne(state.tokens, {
        network,
        address,
        ...tokenData, // All fields from payload are now properly typed
        id,
      });

      state.ui.lastUpdated = Date.now();
    },

    upsertTokens: (state, action: PayloadAction<UpsertTokensPayload>) => {
      const tokens = action.payload.tokens.map((token) => {
        const network = token.network || 'solana';
        const address = token.address;
        const id = tokenKey(network, address);

        return {
          ...token, // Preserve all original data from backend
          id,
        };
      });

      tokensAdapter.upsertMany(state.tokens, tokens);
      state.ui.lastUpdated = Date.now();
    },

    removeToken: (state, action: PayloadAction<string>) => {
      tokensAdapter.removeOne(state.tokens, action.payload);
    },

    clearTokens: (state) => {
      tokensAdapter.removeAll(state.tokens);
      state.ui.lastUpdated = Date.now();
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.ui.isLoading = action.payload;
    },

    setLastUpdated: (state, action: PayloadAction<number>) => {
      state.ui.lastUpdated = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.ui.error = action.payload;
    },
  },
});

export const {
  upsertToken,
  upsertTokens,
  removeToken,
  clearTokens,
  setLoading,
  setError,
  setLastUpdated,
} = tokenSlice.actions;

// ── Auto-generated Entity Selectors ─────────────────────────
export const {
  selectById: selectTokenById,
  selectAll: selectAllTokens,
  selectEntities: selectTokensById,
  selectIds: selectTokenIds,
  selectTotal: selectTokenTotal,
} = tokensAdapter.getSelectors(
  (state: { tokens: TokenState }) => state.tokens.tokens,
);

export const selectTokenBySymbol = createSelector(
  [
    selectAllTokens,
    (_state, network: string, symbol: string) => ({ network, symbol }),
  ],
  (tokens, { network, symbol }) => {
    return (
      tokens.find(
        (token) =>
          token.network === network &&
          token.symbol?.toUpperCase() === symbol.toUpperCase(),
      ) || null
    );
  },
);

export const selectAllTokensByNetwork = createSelector(
  [selectAllTokens, (_state, network: string) => network],
  (tokens, network) => {
    if (!tokens) return [];
    return tokens.filter((token) => token.network === network);
  },
);

export const selectAllTradableTokensByNetwork = createSelector(
  [selectAllTokens, (_state, network: string) => network],
  (tokens, network) => {
    return tokens.filter(
      (token) => token.network === network && token.symbol !== 'USDC',
    );
  },
);

export const selectTokensByCategory = createSelector(
  [selectAllTokens, (_state, category: string) => category],
  (tokens, category) => {
    return tokens.filter((token) =>
      token.categories?.some((cat) => cat.name === category),
    );
  },
);

export const selectActiveTokens = createSelector(
  [selectAllTokens],
  (tokens) => {
    return tokens.filter((token) => token.active !== false);
  },
);

export const selectPartnerTokens = createSelector(
  [selectAllTokens],
  (tokens) => {
    return tokens.filter((token) => token.isPartner === true);
  },
);

export const selectTokenByNetworkAndAddress = createSelector(
  [
    (state: { tokens: TokenState }) => state,
    (_state, network: string, address: string) => ({ network, address }),
  ],
  (state, { network, address }) => {
    const id = tokenKey(network, address);
    return selectTokenById(state, id);
  },
);

export const selectTokenByNetworkAndSymbol = createSelector(
  [
    selectAllTokens,
    (_state, network: string, symbol: string) => ({ network, symbol }),
  ],
  (tokens, { network, symbol }) => {
    return (
      tokens.find(
        (token) =>
          token.network === network && token.symbol === symbol.toUpperCase(),
      ) || null
    );
  },
);

export const selectSolanaUSDCToken = createSelector(
  [selectAllTokens],
  (tokens) => {
    return (
      tokens.find(
        (token) => token.network === 'solana' && token.symbol === 'USDC',
      ) || null
    );
  },
);

// Selector that returns only tokens with non-zero balance, including balance data
export const selectTokensWithBalance = createSelector(
  [selectAllTokens, selectAllBalanceEntities],
  (tokens, balanceEntities): Array<TokenMetadata & { balance: number }> => {
    if (!tokens || !balanceEntities) return [];

    // Create a map of network:address -> total balance (combining embedded + external)
    const keyBalanceMap = new Map<string, number>();

    balanceEntities.forEach((balanceEntity) => {
      // Extract address and network from key format: 'address:network:walletType'
      const [address, network, __] = balanceEntity.id.split(':');
      if (balanceEntity.balance > 0) {
        const key = `${network}:${address}`;
        const currentBalance = keyBalanceMap.get(key) || 0;
        keyBalanceMap.set(key, currentBalance + balanceEntity.balance);
      }
    });

    // Map tokens to their balances
    return tokens
      .map((token) => {
        const key = `${token.network}:${token.address}`;
        const totalBalance = keyBalanceMap.get(key) || 0;
        return {
          ...token,
          balance: totalBalance,
        };
      })
      .filter((token) => token.balance > 0);
  },
);

// Selector that returns tokens with non-zero balance, including balance and USD value data
export const selectTokensWithUSDBalance = createSelector(
  [selectAllTokens, selectAllBalanceEntities],
  (
    tokens,
    balanceEntities,
  ): Array<TokenMetadata & { balance: number; usdValue: number }> => {
    if (!tokens || !balanceEntities) return [];

    // Create a map of network:address -> total balance (combining embedded + external)
    const keyBalanceMap = new Map<string, number>();

    balanceEntities.forEach((balanceEntity) => {
      // Extract address and network from key format: 'address:network:walletType'
      const [address, network, __] = balanceEntity.id.split(':');
      if (balanceEntity.balance > 0) {
        const key = `${network}:${address}`;
        const currentBalance = keyBalanceMap.get(key) || 0;
        keyBalanceMap.set(key, currentBalance + balanceEntity.balance);
      }
    });

    // Map tokens to their balances and USD values
    return tokens
      .map((token) => {
        const key = `${token.network}:${token.address}`;
        const totalBalance = keyBalanceMap.get(key) || 0;
        const usdValue =
          token.usdPrice && totalBalance > 0
            ? totalBalance * token.usdPrice
            : 0;

        return {
          ...token,
          balance: totalBalance,
          usdValue,
        };
      })
      .filter((token) => token.usdValue > 0.01)
      .sort((a, b) => b.usdValue - a.usdValue);
  },
);

// Selector that returns the total USD value of all wallet balances
export const selectTotalWalletBalanceUSD = createSelector(
  [selectTokensWithUSDBalance],
  (tokensWithUSDBalance) => {
    if (!tokensWithUSDBalance || tokensWithUSDBalance.length === 0) {
      return 0;
    }

    const totalValue = tokensWithUSDBalance.reduce((sum, token) => {
      return sum + (token.usdValue || 0);
    }, 0);

    return isNaN(totalValue) ? 0 : totalValue;
  },
);

export const selectTokenUI = (state: { tokens: TokenState }) => state.tokens.ui;

export const selectLastUpdated = (state: { tokens: TokenState }) =>
  state.tokens.ui.lastUpdated;

export default tokenSlice.reducer;
